<template>
  <LoadingSpinner v-if="isLoading" />

  <div v-else-if="isError" class="no-print">
    <div class="text-center mt-5">
      <h1 class="text-2xl">Error loading the invoice</h1>
      <p class="text-lg">Please try again later</p>
    </div>
  </div>

  <div v-else>
    <!-- menu -->
    <div class="flex justify-between items-center bg-white p-4 shadow-md fixed top-0 left-0 right-0 z-10">
      <!-- logo -->
      <div>
        <img :src="logo" alt="uttrly logo" class="h-10" />
      </div>
    </div>

    <div class="w-full">
      <div class="mt-20 max-w-screen-md mx-4 md:mx-8 lg:mx-auto">
        <div class="pt-10 mb-4 md:mb-12 text-white">
          <h2>Invoice for {{ customer.name }}</h2>
          <span v-if="customer?.address" class="mt-2 text-base inline-block">{{ customer.address }}</span>
        </div>

        <!-- invcoie order details -->
        <div class="paper md:mt-4 px-6 pt-8 pb-12 rounded-lg text-sm">
          <!-- header informations -->
          <div class="grid grid-cols-2 mb-8 border-b border-slate-300 pb-4">
            <!-- uttrly informations -->
            <div>
              <div class="mb-4">
                <span class="block font-bold text-md mb-3">Uttrly</span>
                <span class="block mb-2">11810 Grand Park Ave, Suite 500</span>
                <span class="block mb-2">North Bethesda, MD 20852</span>
                <span class="block mb-2">202.681.0825</span>
                <span class="block">portal-admin@uttrly.com</span>
              </div>

              <div class="text-primary-700 font-semibold mb-4 text-2xl">
                <span class="uppercase">Invoice</span>
              </div>

              <div class="text-base font-semibold mb-2">
                <span class="uppercase">Bill to</span>
              </div>

              <!-- customer informations -->
              <div>
                <span v-if="customer?.name" class="block mb-1">{{ customer.name }}</span>
                <span v-if="customer?.address" class="block mb-1">{{ customer.address }}</span>
              </div>
            </div>

            <!-- logo and invcoie header informations -->
            <div>
              <div class="flex justify-end mb-7">
                <img
                  :src="logo"
                  alt="Uttrly"
                />
              </div>
              <div class="text-right">
                <div class="grid grid-cols-4 gap-2">
                  <span class="font-bold col-span-3 uppercase">Invoice #</span>
                  <span>{{ invoice.invoiceId }}</span>
                </div>

                <div class="grid grid-cols-4 gap-2">
                  <span class="font-bold col-span-3 uppercase">DATE</span>
                  <span>{{ getLocalDate(invoice.invoiceDate) }}</span>
                </div>

                <div class="grid grid-cols-4 gap-2">
                  <span class="font-bold col-span-3 uppercase">Due DATE</span>
                  <span>{{ getLocalDate(invoice.dueDate) }}</span>
                </div>

                <div class="grid grid-cols-4 gap-2">
                  <span class="font-bold col-span-3">TERMS</span>
                  <span>Net 30</span>
                </div>
              </div>
            </div>
          </div>

          <div>
            <BaseTable
              :items="invoice.items"
              :fields="invoiceItemFields"
              :show-footer="true"
              class="mt-5"
            >
              <template #unitPrice-data="{ row }">
                {{ currency(row.unitPrice) }}
              </template>

              <template #total-header="{ column }">
                <span class="block text-center">{{ column.label }}</span>
              </template>

              <template #total-data="{ row }">
                <span class="block text-center">{{ currency(row.total) }}</span>
              </template>

              <template #footer>
                <div class="flex items-center justify-between gap-2">
                  <div class="flex-grow">
                    <span class="text-slate-500 text-xs">Thank you for your business and have a great day</span>
                  </div>

                  <div>
                    <div class="flex items-center justify-end gap-2">
                      <span class="font-bold uppercase">Balance due:</span>
                      <span>{{ currency(invoice.balance) }}</span>
                    </div>
                  </div>
                </div>
              </template>
            </BaseTable>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import FirebaseService from '@/services/FirebaseService'
import { useNotification } from '@kyvg/vue3-notification'
import { useQuery } from '@tanstack/vue-query'
import { useHead } from '@unhead/vue'
import { httpsCallable } from 'firebase/functions'
import { extend } from 'underscore'
import { ref } from 'vue'
import { useRoute } from 'vue-router'
import logo from '@/assets/images/logo.png'
import currency from '@/utils/currency'
import { getLocalDate } from '@/utils'
import BaseTable from '@/components/base/BaseTable.vue'
import { Customer } from '@/models/customer'
import { Invoice } from '@/models/invoice'


useHead({ title: 'Invoice' })

const { notify } = useNotification()
const $route = useRoute()
const id = $route.params.id as string

const customer = ref(new Customer())
const invoice = ref(new Invoice())

const invoiceItemFields = [
  { key: 'title', label: 'DESCRIPTION' },
  { key: 'quantity', label: 'QTY' },
  { key: 'unitPrice', label: 'RATE' },
  { key: 'total', label: 'AMOUNT' }
]
const { isLoading, isError } = useQuery({
  queryKey: ['invoice', id],
  queryFn: async () => {
    const functions = await FirebaseService.functions()
    const fetchData = httpsCallable(functions, 'invoice-fetchInvoiceDataV2')
    const response = await fetchData({ id })

    try {
      const data = response.data as any
      customer.value = extend(customer.value, data.customer) as Customer
      invoice.value = extend(invoice.value, data.invoice) as Invoice


      return { invoice: invoice.value, customer: customer.value }
    } catch (err) {
      console.log(err)
      notify({
        type: 'error',
        title: 'Error',
        text: 'Error trying to fetch data'
      })
    }
  }
})

</script>
